import { useRef } from 'react';
import raw from '../../FEATURES.md?raw';
import Icon from './Icon';
import { useApp } from '../store';
import { useEscapeToClose, useFocusTrap } from '../motion';
import { changesSinceLive, groupUnreleased } from '../unreleased';
import { isProjectMode } from '../lifecycle';
import { WHATS_NEW, WHATS_NEW_TOUR_HEADING } from '../whatsnew';

/**
 * WHAT THE BOARD DOES, read straight from FEATURES.md at build time.
 *
 * THIS USED TO SHOW CHANGELOG.md, AND THE AUDIENCES HAD SPLIT. Vinnie, 16
 * August: *"the change log is not meant for development, but meant to state the
 * features that the board currently has and meant for public consumption. So it
 * should change to features only and remain broad and not give details as what
 * was fixed."* Right — a customer wants to know what the software does, not the
 * history of what was once wrong with it, and a list of repairs reads as a list
 * of things that break.
 *
 * So there are two documents now, on purpose, and the old note here argued
 * against exactly that: one file meant the two could never disagree. They can
 * now, and that is the price. It is worth paying because they are no longer the
 * same document — CHANGELOG.md is the engineering record, keeps every fix and
 * every version, and is published nowhere; FEATURES.md is this screen.
 *
 * Everything above the first `##` heading is notes for whoever edits the file,
 * so it is skipped here — same rule as before.
 */
const body = raw.slice(raw.indexOf('\n## '));

type Block = { kind: 'release' | 'para' | 'bullets'; text?: string; items?: string[] };

function parse(md: string): Block[] {
  const out: Block[] = [];
  let bullets: string[] = [];
  // A blank line is the only thing that ends a paragraph or a list in markdown.
  // Without tracking it, every paragraph in a release ran into the one before it,
  // and a bullet long enough to wrap broke into a bullet plus a stray paragraph.
  let broke = true;

  const flush = () => {
    if (bullets.length) out.push({ kind: 'bullets', items: bullets });
    bullets = [];
  };

  for (const line of md.split('\n')) {
    const t = line.trim();
    if (!t) {
      flush();
      broke = true;
      continue;
    }
    if (t.startsWith('## ')) {
      flush();
      out.push({ kind: 'release', text: t.slice(3) });
      broke = true;
      continue;
    }
    if (t === '---') {
      flush();
      broke = true;
      continue;
    }
    if (t.startsWith('- ')) {
      bullets.push(t.slice(2));
      broke = false;
      continue;
    }
    // An unbroken, indented line under a bullet is that bullet continuing.
    if (bullets.length > 0 && !broke && /^\s/.test(line)) {
      bullets[bullets.length - 1] += ' ' + t;
      continue;
    }
    flush();
    const last = out[out.length - 1];
    if (!broke && last?.kind === 'para') last.text += ' ' + t;
    else out.push({ kind: 'para', text: t });
    broke = false;
  }
  flush();
  return out;
}

/**
 * A BULLET THAT OPENS WITH `Added:`, `Improved:` OR `Fixed:` IS DRAWN AS A BADGE.
 *
 * Asked for on 20 August 2026. The word is taken OUT of the sentence rather
 * than left in front of it — a badge beside "Added: Added: Import a backlog"
 * is the sort of thing that only reads wrong once it is in front of somebody.
 *
 * ── AND "IMPROVED" WAS THE MISSING THIRD — 2 September 2026 ─────────────────
 *
 * Two words meant every change was either brand new or a repair, so anything
 * that was neither had to lie in one direction. Something that already worked
 * and now works better read as a bug nobody had noticed, or as a feature
 * arriving for the first time. Vinnie, reviewing the 2.9 notes: *"everything
 * that is a functional fix should just be under improvements or bug fixes."*
 *
 * ⚠️ ORDER MATTERS HERE and it is not alphabetical: `Added` is checked first so
 * that nothing shadows it, and the three are matched on the whole word before
 * the colon. A fourth would need a colour that already means something on this
 * board — see the badge rules in styles.css.
 *
 * Anything else stays a plain bullet, which is most of this file: the evergreen
 * list says what the board does and has no business being tagged.
 */
const TAGS = ['Added', 'Improved', 'Fixed'] as const;
type Tag = (typeof TAGS)[number];

function badgeOf(text: string): { tag?: Tag; rest: string } {
  for (const tag of TAGS) {
    if (text.startsWith(`${tag}:`)) return { tag, rest: text.slice(tag.length + 1).trimStart() };
  }
  return { rest: text };
}

/** Only bold is used in this file, so a full markdown renderer would be overkill. */
function Rich({ text }: { text: string }) {
  return (
    <>
      {text.split(/(\*\*[^*]+\*\*)/g).map((part, n) =>
        part.startsWith('**') && part.endsWith('**') ? (
          <strong key={n}>{part.slice(2, -2)}</strong>
        ) : (
          <span key={n}>{part}</span>
        )
      )}
    </>
  );
}

/**
 * WHAT THIS TEST COPY CARRIES THAT LIVE DOES NOT — shown only where it is true.
 *
 * On live this renders nothing at all, because `__UNRELEASED__` is empty there.
 * That is the whole reason it can sit in the same panel as FEATURES.md without
 * reopening a decision Vinnie already made on 16 August: *"the change log is not
 * meant for development... so it should change to features only and remain broad
 * and not give details as what was fixed."* A customer on live still sees only
 * that. This is engineering detail shown to the one audience that needs it —
 * somebody testing a build that has not shipped.
 *
 * THE TITLES COME FROM THE BOARD, NOT THE COMMIT. A commit names `#269` and
 * whatever the card was called the day it was written; the board knows what it
 * is called now. Reading it live means the list cannot go stale.
 *
 * AND A CARD THIS BOARD DOES NOT HAVE SHOWS ITS NUMBER AND NOTHING ELSE. The
 * numbers are FlightDeck's own — the board FlightDeck is built on. On anybody
 * else's board they match nothing, so an earlier draft that said "not a card on
 * this board" beside each one put that sentence against every row a customer
 * would ever see. It was true and it was noise: it drew the eye to a mismatch
 * that is normal everywhere except one board. The number alone still groups the
 * changes, which is what it is there for.
 */
function Unreleased() {
  const issues = useApp((s) => s.issues);
  if (!__UNRELEASED__.length) return null;

  const titleOf = (ref: string) => issues.find((i) => i.ref === ref)?.title ?? '';
  const { groups, other, listed } = groupUnreleased(__UNRELEASED__, titleOf);
  const total = changesSinceLive(__SINCE_LIVE__);

  return (
    <section className="unreleased">
      <h3 className="unreleased-head">In this test copy, not yet on live</h3>
      <p className="hint">
        Built on live <strong>{__SINCE_LIVE__.split('+')[0]}</strong>, {listed}{' '}
        {listed === 1 ? 'change' : 'changes'} past it.
        {/* Only when the two disagree, and then loudly. Silence here would mean
            a capped list reading as the whole of it. */}
        {total > listed && ` The ${total - listed} oldest are not listed.`}
      </p>
      {groups.map((g) => (
        <div key={g.ref} className="unreleased-card">
          <h4 className="unreleased-ref">
            <span className="ref">{g.ref}</span>
            {g.title}
          </h4>
          <ul className="cl-list">
            {g.changes.map((c, n) => (
              <li key={n}>{c}</li>
            ))}
          </ul>
        </div>
      ))}
      {other.length > 0 && (
        <div className="unreleased-card">
          <h4 className="unreleased-ref">
            Other changes <span className="hint">({other.length})</span>
          </h4>
          <ul className="cl-list">
            {other.map((c, n) => (
              <li key={n}>{c}</li>
            ))}
          </ul>
        </div>
      )}
    </section>
  );
}

/**
 * THE ACTIVE SIDE OF THE CHANGELOG — the curated tour for the current release.
 *
 * Unlike the evergreen FEATURES.md list below, each highlight knows WHERE its
 * feature lives and offers a "Show me" that drills there and highlights the real
 * control (drillTo → revealId, caught by CoachMark). drillTo closes this dialog
 * on its way, so the person lands on the control, not back here. A project-only
 * feature on a continuous board still reads, but carries no "Show me" that would
 * land on a screen that board does not have (#435). See src/whatsnew.ts.
 */
function WhatsNewHighlights() {
  const drillTo = useApp((s) => s.drillTo);
  const isProject = useApp((s) => isProjectMode(s.meta));
  if (!WHATS_NEW.length) return null;
  return (
    <section className="whatsnew">
      <h3 className="whatsnew-head">{WHATS_NEW_TOUR_HEADING}</h3>
      <ul className="whatsnew-list">
        {WHATS_NEW.map((item) => {
          const canShow = !!item.target && (!item.projectOnly || isProject);
          return (
            <li key={item.id} className="whatsnew-item">
              <div className="whatsnew-text">
                <span className="whatsnew-title">{item.title}</span>
                <p className="whatsnew-blurb">{item.blurb}</p>
              </div>
              {canShow && (
                <button
                  type="button"
                  className="btn btn-small whatsnew-show"
                  onClick={() => item.target && drillTo(item.target.view, item.target.reveal)}
                >
                  Show me <Icon name="chevron" size={14} />
                </button>
              )}
            </li>
          );
        })}
      </ul>
    </section>
  );
}

export default function Changelog({ onClose }: { onClose: () => void }) {
  const blocks = parse(body);
  const panelRef = useRef<HTMLDivElement>(null);
  useEscapeToClose(onClose);
  useFocusTrap(panelRef);

  return (
    <div className="overlay" onClick={onClose}>
      <div ref={panelRef} tabIndex={-1} className="modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-head">
          <span className="modal-ref">What FlightDeck does</span>
          <div className="modal-head-actions">
            <button className="btn-icon" onClick={onClose} aria-label="Close">
              <Icon name="close" size={15} />
            </button>
          </div>
        </div>
        <div className="modal-body">
          <p className="hint">
            You are running <strong>v{__APP_VERSION__}</strong>, built from {__BUILD_COMMIT__}.
          </p>
          {/* The active tour for this release leads — it is the thing a person
              opening "What's new" came for, and each item takes them to the real
              control. The evergreen list of what the board does sits below it. */}
          <WhatsNewHighlights />
          {/* Above FEATURES.md, not below it. Somebody opening this on a test
              copy came to find out what they are testing; the evergreen list of
              what the board does is the answer to a different question. */}
          <Unreleased />
          {blocks.map((b, n) => {
            if (b.kind === 'release')
              return (
                <h3 key={n} className="cl-release">
                  {b.text}
                </h3>
              );
            if (b.kind === 'bullets')
              return (
                <ul key={n} className="cl-list">
                  {b.items!.map((it, m) => {
                    const { tag, rest } = badgeOf(it);
                    return (
                      <li key={m}>
                        {tag && (
                          <span className={`cl-badge is-${tag.toLowerCase()}`}>{tag}</span>
                        )}
                        <Rich text={rest} />
                      </li>
                    );
                  })}
                </ul>
              );
            return (
              <p key={n} className="cl-para">
                <Rich text={b.text!} />
              </p>
            );
          })}
        </div>
        <div className="modal-foot">
          <button className="btn" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
